import type { DashboardData, IOU, Recurring, Transaction } from "../../types";
import { normalizeDashboardData } from "../../lib/offline";
import { readPendingEntries } from "../../lib/pending-entries";
import { readPendingReceipts } from "../../lib/pending-receipts";
import { readPendingRecords } from "../../lib/pending-records";

/** Local rows belong to history, without pretending account balances have been confirmed. */
export function withPendingTransactions(data: DashboardData): DashboardData {
  const base = normalizeDashboardData(data);
  const { accounts, transactions, rates, ious, recurring } = base;
  let pending: ReturnType<typeof readPendingEntries>;
  let receipts: ReturnType<typeof readPendingReceipts>;
  let records: ReturnType<typeof readPendingRecords>;
  try {
    pending = readPendingEntries();
    receipts = readPendingReceipts();
    records = readPendingRecords();
  } catch {
    return base;
  }
  const confirmedIds = new Set([
    ...pending.map(({ entry }) => entry.progress?.transactionId).filter(Boolean),
    ...receipts.map(({ receipt }) => receipt.kind === "save" ? receipt.progress?.transactionId : undefined).filter(Boolean),
  ]);
  const fromEntries: Transaction[] = pending.map(({ key, entry }) => {
    const account = accounts.find(a => a.id === entry.accountId);
    const destination = accounts.find(a => a.id === entry.toAccountId);
    const currency = account?.currency || base.baseCurrency;
    const rate = currency === base.baseCurrency ? 1 : rates[currency];
    const toBase = (amount: string) => rate ? Number(amount) * rate : null;
    return {
      id: `local:${entry.requestId}`, pendingKey: key, date: entry.date, time: entry.time,
      type: entry.type || "Expense", accountId: entry.accountId, owner: account?.owner || entry.owner,
      category: entry.category, subcategory: entry.subcategory, title: entry.title,
      description: entry.title, amount: Number(entry.amount), reportableAmount: Number(entry.reportableAmount ?? entry.amount),
      currency, amountBase: toBase(entry.amount), reportableAmountBase: toBase(entry.reportableAmount ?? entry.amount),
      addedBy: entry.owner, recurringId: "", markup: entry.markup || "", notes: entry.notes, ...(entry.pending ? { pending: true } : {}),
      toAccountId: entry.toAccountId, toOwner: destination?.owner,
      receivedAmount: entry.toAccountId ? Number(entry.amount) * Number(entry.exchangeRate || 1) : undefined,
      receivedCurrency: destination?.currency,
      payeeId: entry.payeeId?.startsWith("local:") ? undefined : entry.payeeId,
      tripId: entry.tripId,
      tripDestination: entry.tripDestination,
      allocations: entry.allocations?.map(p => ({ ...p, amount: Number(p.amount), reportableAmount: Number(p.reportableAmount ?? p.amount), amountBase: toBase(p.amount), reportableAmountBase: toBase(p.reportableAmount ?? p.amount) })),
    };
  });
  const fromReceipts: Transaction[] = receipts.flatMap(({ key, receipt }) => {
    if (receipt.kind !== "save") return [];
    const expense = receipt.draft.expense;
    const account = accounts.find(a => a.id === expense.accountId);
    const currency = account?.currency || receipt.draft.currency || base.baseCurrency;
    const rate = currency === base.baseCurrency ? 1 : rates[currency];
    const toBase = (amount: string) => rate ? Number(amount) * rate : null;
    return [{
      id: `local-receipt:${receipt.id}`, pendingKey: key, date: expense.date, time: expense.time,
      type: expense.type || "Expense", accountId: expense.accountId, owner: account?.owner || receipt.owner,
      category: expense.category, subcategory: expense.subcategory, title: expense.title || "Receipt",
      description: expense.title || "Receipt", amount: Number(expense.amount), reportableAmount: Number(expense.reportableAmount ?? expense.amount),
      currency, amountBase: toBase(expense.amount), reportableAmountBase: toBase(expense.reportableAmount ?? expense.amount),
      addedBy: receipt.owner, recurringId: "", markup: expense.markup || "", notes: expense.notes,
      payeeId: expense.payeeId?.startsWith("local:") ? undefined : expense.payeeId,
      tripId: expense.tripId,
      tripDestination: expense.tripDestination,
      allocations: expense.allocations?.map(p => ({ ...p, amount: Number(p.amount), reportableAmount: Number(p.reportableAmount ?? p.amount), amountBase: toBase(p.amount), reportableAmountBase: toBase(p.reportableAmount ?? p.amount) })),
    }];
  });
  const fromLoans: IOU[] = records.flatMap(({ record }) => {
    if (record.kind !== "loan") return [];
    const account = accounts.find((a) => a.id === record.draft.fundingAccountId);
    const amount = Number(record.draft.originalAmount) || 0;
    return [{
      id: `local:loan:${record.id}`,
      payeeId: record.draft.payeeId || `local:${record.draft.payeeName || "payee"}`,
      direction: record.draft.direction,
      originalAmount: amount,
      currency: account?.currency || base.baseCurrency,
      description: record.draft.description || "IOU",
      createdDate: record.draft.createdDate || "",
      dueDate: record.draft.dueDate || "",
      status: "Open" as const,
      createdBy: record.owner,
      fundingAccountId: record.draft.fundingAccountId,
      settlements: [],
      outstanding: amount,
    }];
  });
  const fromBills: Recurring[] = records.flatMap(({ record }) => {
    if (record.kind !== "bill") return [];
    const amount = Number(record.draft.amount) || 0;
    const start = record.draft.startDate || "";
    return [{
      id: `local:bill:${record.id}`,
      name: record.draft.name || "Recurring",
      type: record.draft.type,
      accountId: record.draft.accountId,
      destinationAccountId: record.draft.destinationAccountId || undefined,
      counterpartyId: record.draft.payeeId?.startsWith("local:") ? undefined : (record.draft.payeeId || undefined),
      category: record.draft.category,
      subcategory: record.draft.subcategory,
      owner: record.owner,
      amount,
      currency: record.draft.currency,
      nextDue: record.draft.nextDueDate || start,
      frequency: record.draft.frequency,
      status: "Active",
      active: true,
      startDate: start,
      endDate: record.draft.endDate || undefined,
      reminderDaysBefore: record.draft.reminderDaysBefore ? Number(record.draft.reminderDaysBefore) : undefined,
      updatedAt: Date.now(),
    }];
  });
  return {
    ...base,
    transactions: [...fromEntries, ...fromReceipts, ...transactions.filter(t => !confirmedIds.has(t.id))]
      .sort((a, b) => String(b.date || "").localeCompare(String(a.date || ""))),
    ious: [...fromLoans, ...ious],
    recurring: [...fromBills, ...recurring],
  };
}
